import React, { useEffect, useRef, useState } from 'react';
import { InfoWindow } from '@react-google-maps/api';

import { createMarkerContent } from '../../../components/maps/markerContent';

interface LatLng {
  lat: number;
  lng: number;
}

interface SmoothMarkerProps {
  map: google.maps.Map | null;
  position: LatLng;
  icon?: google.maps.Icon | string;
  title?: string;
  zIndex?: number;
  stationary?: boolean;
  showInfo?: boolean;
  onClick?: () => void;
  onCloseInfo?: () => void;
  children?: React.ReactNode;
}

const MIN_ANIMATE_METERS = 2;
const SNAP_METERS = 700;
const MIN_DURATION_MS = 800;
const MAX_DURATION_MS = 2500;
const MS_PER_METER = 28;

function distanceMeters(from: LatLng, to: LatLng) {
  const earthRadius = 6371000;
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const haversine = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * t;
}

export default function SmoothMarker({
  map,
  position,
  icon,
  title,
  zIndex,
  stationary = false,
  showInfo = false,
  onClick,
  onCloseInfo,
  children
}: SmoothMarkerProps) {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const displayedRef = useRef<LatLng>(position);
  const frameRef = useRef<number>();
  const onClickRef = useRef(onClick);
  const [infoPosition, setInfoPosition] = useState<LatLng>(position);
  onClickRef.current = onClick;

  useEffect(() => {
    if (!map || typeof google === 'undefined' || !google.maps?.marker?.AdvancedMarkerElement) {
      return undefined;
    }

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: displayedRef.current,
      content: createMarkerContent(icon),
      title,
      zIndex
    });
    markerRef.current = marker;

    const clickListener = marker.addListener('click', () => {
      onClickRef.current?.();
    });

    return () => {
      clickListener.remove();
      marker.map = null;
      markerRef.current = null;
    };
  }, [map, icon, title, zIndex]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return undefined;

    const from = displayedRef.current;
    const to = position;
    const meters = distanceMeters(from, to);
    const shouldSnap = stationary || meters < MIN_ANIMATE_METERS || meters > SNAP_METERS;

    const applyPosition = (next: LatLng) => {
      displayedRef.current = next;
      marker.position = next;
      if (showInfo) setInfoPosition(next);
    };

    if (shouldSnap) {
      applyPosition(to);
      return undefined;
    }

    const duration = Math.min(MAX_DURATION_MS, Math.max(MIN_DURATION_MS, meters * MS_PER_METER));
    const startedAt = performance.now();
    const fromLat = from.lat;
    const fromLng = from.lng;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      applyPosition({
        lat: lerp(fromLat, to.lat, progress),
        lng: lerp(fromLng, to.lng, progress)
      });
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [position.lat, position.lng, stationary, showInfo]);

  return (
    <>
      {showInfo && (
        <InfoWindow position={infoPosition} onCloseClick={onCloseInfo}>
          {children}
        </InfoWindow>
      )}
    </>
  );
}

SmoothMarker.defaultProps = {
  icon: undefined,
  title: undefined,
  zIndex: undefined,
  stationary: false,
  showInfo: false,
  onClick: undefined,
  onCloseInfo: undefined,
  children: undefined
};
